// src/pages/Products/ProductList.tsx
import { useMemo } from 'react';
import { Card, Table, Button, Space, message, Modal, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { getProducts, deleteProduct } from '@/api/products';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { usePagination } from '@/hooks/usePagination';
import { getCurrentDeveloperId } from '@/stores/authStore';
import type { Product } from '@/types/product';
// 导入配套图标（✅ 新增 PictureOutlined, CloudUploadOutlined）
import {
  PlusOutlined,
  EditOutlined,
  WarningOutlined,
  PictureOutlined,      // 图片管理图标
  CloudUploadOutlined, // ✅ 新增：OTA 固件图标
} from '@ant-design/icons';

/**
产品列表页面
*/
export const ProductList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { page, size, setPage, setPageSize } = usePagination(1, 20);
  const developerId = getCurrentDeveloperId();

  const productQuery = useQuery({
    queryKey: ['products', developerId, page, size],
    queryFn: () => getProducts(page, size),
    enabled: !!developerId,
  });

  // ✅ 对当前页数据按 created_at 倒序排序（仅当前页）
  const sortedProducts = useMemo(() => {
    if (!productQuery.data?.items) return [];
    return [...productQuery.data.items].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [productQuery.data]);

  const loading = productQuery.isLoading;
  const error = productQuery.error;

  /**
   处理删除产品
   */
  const handleDelete = (productId: string, productName: string): void => {
    Modal.confirm({
      title: '确认删除产品',
      icon: <WarningOutlined className="text-orange-500" />,
      content: `确定要删除产品 "${productName}" 吗？此操作不可恢复，已集成的 App 将无法使用该产品功能！`,
      okType: 'danger',
      okText: '确认删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteProduct(productId);
          message.success('产品删除成功');
          // 清除相关的缓存
          await Promise.allSettled([
            queryClient.invalidateQueries({ queryKey: ['products', developerId] }),
            queryClient.invalidateQueries({ queryKey: ['all-products', developerId] }),
            queryClient.invalidateQueries({ queryKey: ['products'] }),
            queryClient.invalidateQueries({ queryKey: ['app-integrated-products'] }),
          ]);
        } catch (error) {
          console.error('删除产品失败:', error);
          message.error('删除失败，请重试。');
        }
      },
    });
  };

  console.log('products', handleDelete);

  /**
   处理创建产品
   */
  const handleCreate = (): void => {
    navigate('/products/create');
  };

  /**
   处理图片管理
   */
  const handleManageImages = (productId: string): void => {
    navigate(`/products/${productId}/images`);
  };

  /**
   ✅ 新增：处理 OTA 固件管理
   */
  const handleManageOTA = (productId: string): void => {
    navigate(`/products/${productId}/ota`);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert 
        message="加载失败" 
        description="无法加载产品列表，请检查网络或稍后重试。" 
        className="mb-8" 
      />
    );
  }

  // 表格列定义
  const columns: ColumnsType<Product> = [
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: Product) => (
        <Link
          to={`/products/${record.id}`}
          className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
        >
          {text}
        </Link>
      ),
    },
    {
      title: '产品类别',
      dataIndex: 'category',
      key: 'category',
      width: 180,
      render: (_: unknown, record: Product) => (
        <Tag color="blue">{record.category?.name || '未分类'}</Tag>
      ),
    },
    {
      title: '产品 ID',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      render: (id) => <span className="font-mono">{id}</span>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => {
        return new Date(date).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).replace(/\//g, '-');
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 300,  // ✅ 调整宽度以适应新增按钮
      fixed: 'right',
      render: (_: unknown, record: Product) => (
        <Space size="middle" wrap>
          <Button
            size="small"
            type="default"
            icon={<EditOutlined />}
            onClick={() => navigate(`/products/${record.id}/edit`)}
            className="hover:shadow-sm transition-shadow"
          >
            编辑
          </Button>
          
          {/* 图片管理按钮 */}
          <Button
            size="small"
            icon={<PictureOutlined />}
            onClick={() => handleManageImages(record.id)}
            className="hover:shadow-sm transition-shadow"
          >
            图片管理
          </Button>
          
          {/* ✅ 新增：OTA 固件管理按钮 */}
          <Button
            size="small"
            icon={<CloudUploadOutlined />}
            onClick={() => handleManageOTA(record.id)}
            className="hover:shadow-sm transition-shadow"
          >
            OTA 固件
          </Button>
          
          {/* 删除按钮已注释，如需启用可取消注释 */}
          {/* 
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id, record.name)}
            className="hover:shadow-sm transition-shadow"
          >
            删除
          </Button>
          */}
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full space-y-8">
      {/* 标题 + 操作区 - 适配小屏 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">产品管理</h1>
          <p className="text-gray-600 mt-1">
            管理所有产品的创建、编辑、删除及图片管理，支持产品与 App 集成。
          </p>
        </div>
        <Button
          type="primary"
          onClick={handleCreate}
          icon={<PlusOutlined />}
          className="px-6 hover:shadow-md transition-shadow"
        >
          创建产品
        </Button>
      </div>

      <Card
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
        style={{ padding: '0px' } as React.CSSProperties}
      >
        <Table
          dataSource={sortedProducts}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: size,
            total: productQuery.data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: setPage,
            onShowSizeChange: (_, newSize) => setPageSize(newSize),
            showTotal: (total) => `共 ${total} 个产品`,
            className: 'py-4',
          }}
          bordered={false}
          size="middle"
          scroll={{ x: 'max-content' }}
          style={{
            '--ant-table-row-hover-bg': '#f5faff',
            '--ant-table-header-text-color': '#667085',
            '--ant-table-border-color': '#e8e8e8',
          } as React.CSSProperties}
          locale={{
            emptyText: '暂无产品数据，点击右上角「创建产品」添加第一个产品吧。',
          }}
        />
      </Card>
    </div>
  );
};